/**
 * MenusSemaineEncart — affichage lecture seule des menus de la semaine, pour
 * l'espace famille. Pas d'édition ; navigation semaine simple.
 */

import { useEffect, useMemo, useState } from 'react';
import { UtensilsCrossed, FileText, ChevronDown } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Badge } from '@/components/ui';
import { MENU_TYPES, ALLERGENES_INCO } from '@roronoa-collectivites/shared-types';
import type { Menu, MenuType, AllergeneInco } from '@roronoa-collectivites/shared-types';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
function startOfWeek(d = new Date()): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dow = x.getDay() === 0 ? 7 : x.getDay(); x.setDate(x.getDate() - (dow - 1)); return x;
}
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

export function MenusSemaineEncart() {
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [replie, setReplie] = useState(false);

  async function ouvrirPdf() {
    setPdfBusy(true);
    try {
      const blob = await api.scolaire.menus.pdfBlob(ymd(weekStart));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { /* silencieux côté famille */ }
    finally { setPdfBusy(false); }
  }

  const weekDays = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.scolaire.menus.list(ymd(weekStart), ymd(addDays(weekStart, 4)));
        if (!cancelled) setMenus(r);
      } catch (e) { if (!cancelled) setError(e instanceof ApiException ? e.message : 'Erreur'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [weekStart]);

  const menuDuJour = (day: Date) => menus.find((m) => m.dateMenu === ymd(day)) ?? null;
  const typeLabel = (t: MenuType) => MENU_TYPES.find((x) => x.value === t)?.label ?? t;
  const allergeneLabel = (a: AllergeneInco) => ALLERGENES_INCO.find((x) => x.value === a)?.label ?? a;

  // N'affiche rien si la semaine est entièrement vide (évite un bloc vide chez les familles).
  if (!loading && menus.length === 0 && error === null) return null;

  return (
    <Card className="mb-6">
      <CardBody>
        <div className="mb-3 flex items-center justify-between gap-2">
          <button type="button" onClick={() => setReplie((v) => !v)} aria-expanded={!replie}
            className="flex items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md">
            <ChevronDown className={`h-4 w-4 text-ink-muted transition-transform duration-200 ${replie ? '-rotate-90' : ''}`} aria-hidden="true" />
            <UtensilsCrossed className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
            <h2 className="font-display text-lg font-semibold text-ink">Menus de la semaine</h2>
          </button>
          {!replie && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => void ouvrirPdf()} disabled={pdfBusy} aria-label="PDF des menus de la semaine">
              <FileText className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>←</Button>
            <span className="text-xs text-ink-muted">{weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>→</Button>
          </div>
          )}
        </div>

        {replie ? null : loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {weekDays.map((day, idx) => {
              const m = menuDuJour(day);
              return (
                <div key={ymd(day)} className="rounded-r-xl border border-border bg-surface/40 p-2.5">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink">{JOURS[idx]} {day.getDate()}</span>
                    {m && <Badge>{typeLabel(m.typeMenu)}</Badge>}
                  </div>
                  {m ? (
                    <div className="space-y-0.5 text-[11px] text-ink-muted">
                      {m.entree && <p>{m.entree}</p>}
                      {m.plat && <p className="text-ink">{m.plat}</p>}
                      {m.accompagnement && <p>{m.accompagnement}</p>}
                      {m.dessert && <p>{m.dessert}</p>}
                      {m.allergenes.length > 0 && <p className="pt-0.5 text-cantine-fg">⚠ {m.allergenes.map(allergeneLabel).join(', ')}</p>}
                    </div>
                  ) : (
                    <p className="text-[11px] text-ink-subtle">—</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
